// ─── Éléments POSSÉDÉS — compteur unique du quota (Core) ──────────
// Le quota de jetons borne ce qu'on POSSÈDE, tous types confondus : calendriers,
// inventaires, plans… Sans ce compteur commun, chaque écran comptait son propre
// type et l'app annonçait « 2/2 » à quelqu'un qui possédait 4 éléments.
//
// Le Core ne connaît pas les collections des modules : l'APP les déclare, avec
// LA REQUÊTE à utiliser. Ce point n'est pas un détail — toutes les collections
// ne se laissent pas interroger de la même façon :
//   • `calendriers` est lisible par tout compte connecté → filtre `creePar` ;
//   • `inventaires` / `plans` ne sont lisibles que via `acces array-contains`,
//     la seule requête que leurs règles savent prouver. Interroger `creePar`
//     dessus fait échouer la LISTE ENTIÈRE (règle non prouvable), et le compteur
//     tombe à zéro sans rien dire.
// On filtre donc `creePar === uid` côté client après coup : la requête sert à
// obtenir un lot autorisé, pas à faire le tri.
//
//   const { sources, erreurs, pret } = useElementsPossedes([
//     { cle: 'calendrier', collection: 'calendriers', filtre: ['creePar', '=='] },
//     { cle: 'inventaire', collection: 'inventaires', filtre: ['acces', 'array-contains'] },
//   ]);
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { normaliserElement, proprietaireDe } from '../services/elements';

export function useElementsPossedes(defs = []) {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const [parCle, setParCle] = useState({});
  const [erreurs, setErreurs] = useState({});

  // Signature stable : re-souscrire seulement si la LISTE des collections change.
  const cles = defs.map(d => `${d.cle}:${d.collection}`).join('|');

  useEffect(() => {
    if (!uid) { setParCle({}); setErreurs({}); return undefined; }
    const stops = defs.map(d => {
      const [champ, op] = d.filtre || ['creePar', '=='];
      return onSnapshot(
        query(collection(db, d.collection), where(champ, op, uid)),
        (snap) => {
          const mien = snap.docs
            .map(doc => normaliserElement({ id: doc.id, ...doc.data() }, d.cle))
            .filter(e => proprietaireDe(e) === uid);   // POSSÉDÉS seulement
          setParCle(prev => ({ ...prev, [d.cle]: mien }));
          setErreurs(prev => (prev[d.cle] ? { ...prev, [d.cle]: null } : prev));
        },
        (e) => setErreurs(prev => ({ ...prev, [d.cle]: e?.code || 'erreur' })),
      );
    });
    return () => stops.forEach(s => s && s());
  }, [uid, cles]); // eslint-disable-line react-hooks/exhaustive-deps

  const sources = useMemo(() => defs.map(d => ({
    cle: d.cle, label: d.label, emoji: d.emoji,
    elements: (parCle[d.cle] || []).map(e => ({ ...e, proprietaire: true })),
  })), [cles, parCle]); // eslint-disable-line react-hooks/exhaustive-deps

  const listeErreurs = useMemo(
    () => Object.entries(erreurs).filter(([, v]) => !!v).map(([k, v]) => `${k}:${v}`),
    [erreurs]);

  // `pret` = chaque collection a répondu (ou échoué). Avant ça, le compteur est
  // incomplet : on ne conclut pas « il reste de la place » sur une liste vide.
  const pret = defs.every(d => parCle[d.cle] !== undefined || erreurs[d.cle]);

  return { sources, erreurs: listeErreurs, pret };
}

export default useElementsPossedes;
